import React, { useCallback, useEffect, useMemo, useState } from "react";
import DataTable, { SampleStatusCell } from "../components/common/DataTable";
import ConfirmModal from "../components/common/ConfirmModal";
import { OrganizerSummary } from "../types/admin";
import { useAdminApi } from "../hooks/useAdminApi";
import { toast } from "react-toastify";

interface OrganizerRow {
	id: number;
	external_id?: string;
	organizer_name: string;
	email: string;
	verification_status?: "pending" | "approved" | "rejected" | null;
	platform_fee_rate?: number | null;
	payment_fee_rate?: number | null;
}

type VerifyAction = "approve" | "reject" | "pending";

const formatPercent = (value?: number | null) =>
	value === undefined || value === null ? "-" : `${value}%`;

const OrganizersPage: React.FC = () => {
	const api = useAdminApi();
	const [searchInput, setSearchInput] = useState("");
	const [keyword, setKeyword] = useState("");
	const [statusFilter, setStatusFilter] = useState<string>("");
	const [rows, setRows] = useState<OrganizerRow[]>([]);
	const [page, setPage] = useState(1);
	const [pageSize, setPageSize] = useState(20);
	const [total, setTotal] = useState(0);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const [confirmOpen, setConfirmOpen] = useState(false);
	const [confirmAction, setConfirmAction] = useState<VerifyAction | null>(null);
	const [target, setTarget] = useState<OrganizerRow | null>(null);
	const [actionLoading, setActionLoading] = useState(false);

	const [feeModalOpen, setFeeModalOpen] = useState(false);
	const [feeForm, setFeeForm] = useState({
		platform_fee_rate: "0",
		payment_fee_rate: "0",
	});

	const mapOrganizer = useCallback(
		(org: OrganizerSummary): OrganizerRow => ({
			id: org.id,
			external_id: org.external_id,
			organizer_name: org.organizer_name,
			email: org.email,
			verification_status: org.verification_status ?? null,
			platform_fee_rate:
				org.platform_fee_rate !== undefined && org.platform_fee_rate !== null
					? Number(org.platform_fee_rate)
					: null,
			payment_fee_rate:
				org.payment_fee_rate !== undefined && org.payment_fee_rate !== null
					? Number(org.payment_fee_rate)
					: null,
		}),
		[]
	);

	const fetchOrganizers = useCallback(async () => {
		setLoading(true);
		setError(null);
		try {
			const res = await api.listOrganizers({
				organizer_name: keyword || undefined,
				email: keyword || undefined,
				is_verified: statusFilter || undefined,
				page,
				per_page: pageSize,
			});

			const mapped = res.data.map(mapOrganizer);
			setRows(mapped);
			setTotal(res.total);
			setPageSize(res.per_page);
		} catch (err) {
			console.warn("Failed to load organizers", err);
			setError("オーガナイザー一覧の取得に失敗しました");
		} finally {
			setLoading(false);
		}
	}, [api, keyword, statusFilter, page, pageSize, mapOrganizer]);

	useEffect(() => {
		fetchOrganizers();
	}, [fetchOrganizers]);

	const columns = useMemo(
		() => [
			{ key: "external_id", header: "ID" },
			{ key: "organizer_name", header: "名称" },
			{ key: "email", header: "メール" },
			{
				key: "verification_status",
				header: "審査",
				render: (row: OrganizerRow) => (
					<SampleStatusCell status={row.verification_status ?? "pending"} />
				),
			},
			{
				key: "platform_fee_rate",
				header: "PF手数料",
				render: (row: OrganizerRow) => formatPercent(row.platform_fee_rate),
			},
			{
				key: "payment_fee_rate",
				header: "支払手数料",
				render: (row: OrganizerRow) => formatPercent(row.payment_fee_rate),
			},
			{
				key: "actions",
				header: "操作",
				render: (row: OrganizerRow) => (
					<div className="flex flex-wrap gap-2 text-xs font-semibold">
						<button
							className="text-primary"
							onClick={() => {
								setTarget(row);
								setConfirmAction("approve");
								setConfirmOpen(true);
							}}
						>
							承認
						</button>
						<button
							className="text-rose-600"
							onClick={() => {
								setTarget(row);
								setConfirmAction("reject");
								setConfirmOpen(true);
							}}
						>
							却下
						</button>
						<button
							className="text-muted"
							onClick={() => {
								setTarget(row);
								setConfirmAction("pending");
								setConfirmOpen(true);
							}}
						>
							保留に戻す
						</button>
						<button
							className="text-indigo-600"
							onClick={() => {
								setTarget(row);
								setFeeForm({
									platform_fee_rate: String(row.platform_fee_rate ?? 0),
									payment_fee_rate: String(row.payment_fee_rate ?? 0),
								});
								setFeeModalOpen(true);
							}}
						>
							手数料編集
						</button>
					</div>
				),
			},
		],
		[]
	);

	const handleVerify = async (reason?: string) => {
		if (!target || !confirmAction) return;
		const status =
			confirmAction === "approve"
				? "approved"
				: confirmAction === "reject"
				? "rejected"
				: "pending";
		setActionLoading(true);
		try {
			const response = await api.verifyOrganizer(target.id, {
				status,
				note: reason ?? undefined,
			});
			const updated = mapOrganizer(
				(response.data?.data ?? response.data) as OrganizerSummary
			);
			setRows((prev) =>
				prev.map((row) => (row.id === updated.id ? updated : row))
			);
			toast.success(`審査ステータスを ${status} に更新しました`);
		} catch (err) {
			console.error("Failed to verify organizer", err);
			toast.error("審査ステータスの更新に失敗しました");
		} finally {
			setActionLoading(false);
			setConfirmOpen(false);
			setConfirmAction(null);
			setTarget(null);
		}
	};

	const handleFeeSave = async () => {
		if (!target) return;
		setActionLoading(true);
		try {
			await api.updateOrganizerFee(target.id, {
				platform_fee_rate: Number(feeForm.platform_fee_rate),
				payment_fee_rate: Number(feeForm.payment_fee_rate),
			});
			toast.success("手数料を更新しました");
			setFeeModalOpen(false);
			fetchOrganizers();
		} catch (err) {
			console.error("Failed to update fee", err);
			toast.error("手数料の更新に失敗しました");
		} finally {
			setActionLoading(false);
		}
	};

	const handleSearch = () => {
		setPage(1);
		setKeyword(searchInput.trim());
	};

	return (
		<div className="space-y-4">
			<div>
				<h1 className="text-xl font-semibold text-ink">Organizers</h1>
				<p className="text-sm text-muted">
					承認/却下、手数料率の変更、銀行口座チェック
				</p>
			</div>

			<div className="rounded-2xl border border-surface-border bg-white p-4 shadow-card">
				<div className="grid gap-3 md:grid-cols-4">
					<input
						className="input"
						placeholder="名称 / メール / external_id"
						value={searchInput}
						onChange={(e) => setSearchInput(e.target.value)}
					/>
					<select
						className="input"
						value={statusFilter}
						onChange={(e) => {
							setStatusFilter(e.target.value);
							setPage(1);
						}}
					>
						<option value="">審査状態</option>
						<option value="true">verified=true</option>
						<option value="false">verified=false</option>
					</select>
					<div className="md:col-span-2 flex items-center gap-2">
						<button
							className="rounded-xl bg-primary px-3 sm:px-4 py-2 text-sm font-semibold text-white"
							onClick={handleSearch}
						>
							検索
						</button>
						<span className="text-xs text-muted">is_verified で絞り込み</span>
					</div>
				</div>

				<div className="mt-4">
					<DataTable
						data={rows}
						columns={columns}
						total={total}
						page={page}
						pageSize={pageSize}
						onPageChange={setPage}
						loading={loading}
						error={error}
					/>
				</div>
			</div>

			<ConfirmModal
				open={confirmOpen}
				title={
					confirmAction === "approve"
						? "このオーガナイザーを承認しますか？"
						: confirmAction === "reject"
						? "却下しますか？"
						: "保留に戻しますか？"
				}
				message={target?.organizer_name}
				confirmLabel="実行する"
				loading={actionLoading}
				requireReason
				onConfirm={handleVerify}
				onCancel={() => setConfirmOpen(false)}
			/>

			{feeModalOpen && target && (
				<div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
					<div className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-2xl">
						<h3 className="text-lg font-semibold text-ink">手数料を更新</h3>
						<p className="mt-1 text-sm text-muted">{target.organizer_name}</p>

						<div className="mt-4 grid gap-3 md:grid-cols-2">
							<label className="text-sm text-muted">
								プラットフォーム手数料 (%)
								<input
									type="number"
									step="0.1"
									min="0"
									max="100"
									value={feeForm.platform_fee_rate}
									onChange={(e) =>
										setFeeForm({
											...feeForm,
											platform_fee_rate: e.target.value,
										})
									}
									className="mt-1 w-full rounded-xl border border-surface-border bg-surface-alt px-3 py-2 text-sm focus:border-primary focus:outline-none"
								/>
							</label>
							<label className="text-sm text-muted">
								決済手数料 (%)
								<input
									type="number"
									step="0.1"
									min="0"
									max="100"
									value={feeForm.payment_fee_rate}
									onChange={(e) =>
										setFeeForm({ ...feeForm, payment_fee_rate: e.target.value })
									}
									className="mt-1 w-full rounded-xl border border-surface-border bg-surface-alt px-3 py-2 text-sm focus:border-primary focus:outline-none"
								/>
							</label>
						</div>

						<div className="mt-6 flex justify-end gap-2">
							<button
								onClick={() => setFeeModalOpen(false)}
								className="rounded-lg border border-surface-border px-3 sm:px-4 py-2 text-sm font-semibold text-muted"
							>
								キャンセル
							</button>
							<button
								onClick={handleFeeSave}
								className="rounded-lg bg-primary px-3 sm:px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
								disabled={actionLoading}
							>
								{actionLoading ? "更新中..." : "保存"}
							</button>
						</div>
					</div>
				</div>
			)}
		</div>
	);
};

export default OrganizersPage;
